/**
 * Header.tsx
 *
 * Global site header including:
 * - Logo link
 * - Desktop navigation bar
 * - Mobile navigation drawer
 * - Profile dropdown/settings menu
 *
 * Layout:
 * - Fixed top bar with backdrop blur
 * - Responsive content grid (logo | nav | profile)
 * - Invisible spacer div maintains page layout height
 *
 * Compatible with:
 * - Node 20–24
 * - React 18+
 * - Next.js 13/14 (App or Pages Router)
 */

import { FC, memo, useState } from "react";

import { Link } from "components/link";
import { DesktopNav } from "components/layout/header.desktop-nav";
import { MobileNav } from "components/layout/header.mobile-nav";
import { ProfileNav } from "components/layout/header.settings";

import { HEADER } from "content/layout";

export const Header: FC = memo(() => {
  const [showNav, setShowNav] = useState(false);

  return (
    <>
      {/* Fixed Navigation Bar */}
      <header
        className="
          fixed inset-x-0 top-0 z-50 h-20 w-full 
          border-b border-gray-800/10 bg-white/50 backdrop-blur 
          dark:border-gray-100/10 dark:bg-gray-900/40
          print:hidden
        "
      >
        <div
          className="
            mx-auto flex h-full max-w-6xl items-center 
            grid-cols-[210px_1fr_210px] gap-1 px-4 
            md:grid md:gap-4 md:px-8
          "
        >
          {/* Logo */}
          <Link
            href="/"
            className="z-10 w-min"
            data-tip="Hi, I'm Rhae. Welcome to my site."
            data-delay-show={2000}
          >
            <span className="sr-only">mattumm.al Logo</span>
            {HEADER.logo.title}
          </Link>

          {/* Desktop Navigation */}
          <DesktopNav />

          {/* Profile / Settings */}
          <ProfileNav showNav={showNav} />

          {/* Mobile Menu Button + Drawer */}
          <MobileNav showNav={showNav} setShowNav={setShowNav} />
        </div>

        {/* Optional Background Decoration Layer */}
        <div className="background pointer-events-none absolute inset-0 z-0 select-none">
          {/* 
            Uncomment if you want vertical grid lines:
            
            <div className="absolute bottom-0 h-px w-full bg-[linear-gradient(90deg,var(--line-color),var(--line-color)_50%,transparent_0,transparent)] bg-[length:8px_1px] dark:[--line-color:theme(colors.gray.700/20)]" />

            <div className="relative mx-auto grid h-full max-w-6xl grid-cols-4 px-4">
              <div className="h-full w-px bg-[color:var(--line-color)]" />
              <div className="h-full w-px bg-[linear-gradient(180deg,var(--line-color),var(--line-color)_50%,transparent_0,transparent)]" />
              <div className="h-full w-px bg-[linear-gradient(180deg,var(--line-color),var(--line-color)_50%,transparent_0,transparent)]" />
              <div className="h-full w-px bg-[linear-gradient(180deg,var(--line-color),var(--line-color)_50%,transparent_0,transparent)]" />
              <div className="absolute right-4 top-0 h-full w-px bg-[color:var(--line-color)]" />
            </div>
          */}
        </div>
      </header>

      {/* Spacer: prevents layout shift from fixed header */}
      <div className="pointer-events-none h-20 select-none transition print:hidden" />
    </>
  );
});

Header.displayName = "Header";

export default Header;
